/**
 * #1029 / #1098 — last zone + volume live in ~/.maestro on the Connector.
 */

import { isConnectorAvailable } from '../lib/connector';
import { LOCAL_ZONE_STORAGE_ID, isLocalZoneStorageId } from '../utils/localZoneStorage';
import { isUnusableZoneDisplayName } from '../utils/zoneDisplayName';
import { SAVED_ZONE_META_KEY } from './zoneRestorePick';

export const SAVED_ZONE_VOLUME_KEY = 'maestro_saved_zone_volume';
const CONNECTOR_ZONE_STORAGE_KEY = 'maestro_connector_active_zone';

export type LastPlaybackSession = {
  zone_id: string;
  zone_name: string;
  zone_type?: string;
  volume?: number | null;
};

async function invokeLastPlaybackSession(): Promise<LastPlaybackSession | null> {
  const invoke = (window as unknown as { __TAURI__?: { core?: { invoke?: (c: string) => Promise<unknown> } } })
    .__TAURI__?.core?.invoke;
  if (!invoke) return null;
  try {
    const session = (await invoke('get_last_playback_session')) as LastPlaybackSession | null;
    if (!session?.zone_id?.trim()) return null;
    return session;
  } catch {
    return null;
  }
}

export async function pullLastPlaybackSessionFromDisk(): Promise<LastPlaybackSession | null> {
  if (!isConnectorAvailable()) return null;
  try {
    const { waitForIpcTransportReady } = await import('../lib/connector');
    await waitForIpcTransportReady();
  } catch {
    return null;
  }
  return invokeLastPlaybackSession();
}

export function seedLocalStorageFromLastPlaybackSession(session: LastPlaybackSession): void {
  if (typeof localStorage === 'undefined') return;
  const zoneId = isLocalZoneStorageId(session.zone_id) ? LOCAL_ZONE_STORAGE_ID : session.zone_id;
  const name = String(session.zone_name || '').trim();
  try {
    localStorage.setItem(CONNECTOR_ZONE_STORAGE_KEY, zoneId);
    localStorage.setItem(
      SAVED_ZONE_META_KEY,
      JSON.stringify({
        id: zoneId,
        name: name && !isUnusableZoneDisplayName(name, zoneId) ? name : zoneId,
        type: session.zone_type || 'upnp',
      }),
    );
    if (session.volume != null && session.volume >= 0 && session.volume <= 100) {
      localStorage.setItem(SAVED_ZONE_VOLUME_KEY, String(session.volume));
    }
  } catch { /* quota */ }
}

export async function hydrateConnectorZoneStorageFromDisk(): Promise<boolean> {
  const { ensureBootSessionHydratedFromDisk } = await import('./bootSessionHydrate');
  return ensureBootSessionHydratedFromDisk();
}

export function readSavedZoneVolume(): number | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SAVED_ZONE_VOLUME_KEY);
    if (!raw) return null;
    const vol = parseInt(raw, 10);
    if (Number.isNaN(vol) || vol < 0 || vol > 100) return null;
    return vol;
  } catch {
    return null;
  }
}
